import type { TagVariant } from '@sunwebgroup/uds-ui/tag'
import { Tag } from '@sunwebgroup/uds-ui/tag'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import { identity } from 'ramda'
import * as React from 'react'
import { useStore } from 'zustand'
import { useIntl } from '@/intl'
import {
  type GroupResponsibleStore,
  type Label,
  type ParticipantFilterType,
  ParticipantFilterValues,
} from '@/scopes/account/group-responsible/domain/types'
import { useGroupResponsibleStore } from '@/scopes/account/group-responsible/infrastructure/useGroupResponsibleStore'
import { useGroupResponsibleDependencies } from '../dependencies'
import { getFilteredParticipants, getParticipantCounts } from '../selectors'
import { getParticipantStatusLabel } from '../utils/participants'
import { FilterToggleGroup } from './FilterToggleGroup'
import { ParticipantExtraInfoSheetPortal } from './ParticipantExtraInfoSheetPortal'
import styles from './ParticipantTable.module.css'
import { RoomAssignmentStatusBanner } from './RoomAssignmentStatusBanner'
import {
  ChevronCell,
  EmptyTableMessage,
  Subtitle,
  Table,
  TableCell,
  TableCellWithSubtitle,
  TableHead,
  TableRow,
} from './Table'
import { TableSearch } from './TableSearch'

const breakpointHideCol = 'primitives_breakpoint_m_max'

const TAG_VARIANT_BY_STATUS: Record<Label['type'], TagVariant> = {
  brand: 'primary-tinted',
  canceled: 'danger-tinted',
  discount: 'neutral-tinted',
  error: 'danger-tinted',
  info: 'informative-tinted',
  success: 'success-tinted',
  warning: 'warning-tinted',
}

export function ParticipantTable() {
  const [filter, setFilter] = React.useState<ParticipantFilterType>('all')
  const [searchTerm, setSearchTerm] = React.useState('')
  const counts = useGroupResponsibleStore(getParticipantCounts)
  const filteredParticipants = useGroupResponsibleStore(state => getFilteredParticipants(state, filter, searchTerm))
  const { groupResponsibleStore } = useGroupResponsibleDependencies()
  const store = useStore(groupResponsibleStore, identity<GroupResponsibleStore>)
  const intl = useIntl()
  const isOpenSubscriptionModel = useGroupResponsibleStore(state => state.bookingData.group.subscriptionModel === 'Open')
  type CountKeys = keyof ReturnType<typeof getParticipantCounts>
  const filters: { value: ParticipantFilterType, label: string, countKey: CountKeys }[] = [
    { value: ParticipantFilterValues.ALL, label: 'groups_responsible_all', countKey: 'all' },
    { value: ParticipantFilterValues.NOT_PAID, label: 'groups_responsible_not_paid', countKey: 'notPaid' },
    { value: ParticipantFilterValues.PARTIAL_PAID, label: 'groups_responsible_partial_paid', countKey: 'partialPaid' },
    { value: ParticipantFilterValues.PAID, label: 'groups_responsible_paid', countKey: 'paid' },
  ]

  function handleSearchChange(event: React.ChangeEvent<HTMLInputElement>) {
    setSearchTerm(event.target.value)
  }

  return (
    <>
      <TableSearch searchTerm={searchTerm} onSearchChange={handleSearchChange} />
      <FilterToggleGroup filter={filter} filters={filters} counts={counts} onFilterChange={setFilter} />
      <RoomAssignmentStatusBanner />
      {filteredParticipants.length === 0
        ? (
            <EmptyTableMessage />
          )
        : (
            <>
              <Table>
                <thead>
                  <TableRow>
                    <TableHead>
                      <Typography variant="body-s-regular-tight">
                        {intl.formatMessage({ id: 'groups_responsible_table_name_label' })}
                      </Typography>
                    </TableHead>
                    {isOpenSubscriptionModel && (
                      <TableHead className={styles.styledOnBreakpoint}>
                        <Typography variant="body-s-regular-tight">
                          {intl.formatMessage({ id: 'common_status' })}
                        </Typography>
                      </TableHead>
                    )}
                    <TableHead $hideOn={isOpenSubscriptionModel ? breakpointHideCol : 'primitives_breakpoint_m_min'}>
                      <Typography variant="body-s-regular-tight">
                        {intl.formatMessage({ id: 'groups_responsible_room' })}
                      </Typography>
                    </TableHead>
                    {isOpenSubscriptionModel && (
                      <TableHead $hideOn={breakpointHideCol} $alignRight>
                        <Typography variant="body-s-regular-tight">
                          {intl.formatMessage({ id: 'groups_responsible_table_amount_due_label' })}
                        </Typography>
                      </TableHead>
                    )}
                    <TableHead $hideOn={breakpointHideCol} />
                  </TableRow>
                </thead>
                <tbody>
                  {filteredParticipants.map((participant) => {
                    const { type: statusType, text: statusText } = getParticipantStatusLabel(participant)
                    const shouldShowSign = participant.amountDue < 0

                    return (
                      <TableRow
                        key={participant.id}
                        onClick={() => {
                          store.changeExtraInfoId(participant.id)
                        }}
                      >
                        <TableCellWithSubtitle>
                          <div>{participant.name}</div>
                          <Subtitle>{participant.email}</Subtitle>
                        </TableCellWithSubtitle>
                        {isOpenSubscriptionModel && (
                          <TableCell className={styles.styledOnBreakpoint}>
                            <Tag variant={TAG_VARIANT_BY_STATUS[statusType]}>
                              {intl.formatMessage({ id: statusText })}
                            </Tag>
                          </TableCell>
                        )}
                        <TableCell $hideOn={isOpenSubscriptionModel ? breakpointHideCol : 'primitives_breakpoint_m_min'}>{intl.formatMessage({ id: participant.hasRoomAssigned ? 'groups_responsible_room_assigned' : 'groups_responsible_room_unassigned' })}</TableCell>
                        {isOpenSubscriptionModel && (
                          <TableCell $hideOn={breakpointHideCol} $alignRight>
                            {
                              intl.formatCurrency(participant.amountDue, { showSign: shouldShowSign })
                            }
                          </TableCell>
                        )}
                        <ChevronCell $hideOn={breakpointHideCol} />
                      </TableRow>
                    )
                  })}
                </tbody>
              </Table>
              <ParticipantExtraInfoSheetPortal />
            </>
          )}
    </>
  )
}
